/**
 * @file useUsbSpeedCheck.ts
 * @description USB 3.0 连接检测：纯 WebHID 读取 GetInfo(0x03)，无需操作键盘、
 * 无需 WebUSB/原生 IPC 授权。
 *
 * GetInfo(0x03) 数据区偏移 6：bit0=是否安装 16K 驱动，bit1=USB 版本（0=USB2.0，1=USB3.0）。
 */

import { GCOME_AP_CMD } from '@rapoo/device-sdk'
import i18n from '@/locales'

/** USB 3.0 检测结果。 */
export interface UsbSpeedCheckResult {
  /** 是否为 USB 3.0（null = 无法判定）。 */
  isUsb3: boolean | null
  /** 判定来源：hid（GetInfo 只读字段）| webusb（USB 描述符）| unknown。 */
  source: 'hid' | 'webusb' | 'unknown'
  /** 展示文案（含原始状态字节）。 */
  usbVersion?: string
  /** GetInfo 完整数据区原始值（[0-1] 版本、[2-5] 预留、[6] USB 状态、[7+] 编译时间串）。 */
  raw?: number[]
  has16kDriver?: boolean
}

/** GetInfo 数据区偏移 6。 */
const USB_STATUS_BYTE = 6

function t(key: string, params?: Record<string, unknown>): string {
  return i18n.global.t(key, params ?? {})
}

/** 字节数组 → "aa bb cc dd" 十六进制串。 */
function bytesHex(bytes: number[]): string {
  return bytes.map((b) => b.toString(16).padStart(2, '0')).join(' ')
}

/**
 * 检测当前连接是否为 USB 3.0（纯 WebHID，只读 GetInfo，不写键盘）。
 * @param device 当前 GCOME_AP 设备（BaseDevice）
 */
export async function checkUsb3(device: unknown): Promise<UsbSpeedCheckResult> {
  const target = device as {
    sendRawApCommand(req: {
      command: number
      data?: number[]
    }): Promise<{ response: { data: Uint8Array } }>
  }
  try {
    const res = await target.sendRawApCommand({
      command: GCOME_AP_CMD.getInfo,
      data: new Array(56).fill(0),
    })
    const data = res.response.data
    if (data.length <= USB_STATUS_BYTE) {
      return { isUsb3: null, source: 'hid' }
    }
    const status = data[USB_STATUS_BYTE]
    const raw = Array.from(data.slice(0, data.length))
    const has16kDriver = Boolean(status & 0x01)
    const isUsb3 = Boolean(status & 0x02)
    return {
      isUsb3,
      source: 'hid',
      has16kDriver,
      usbVersion: t('performance.usbSpeed.getInfoDetail', {
        usbLabel: isUsb3 ? t('performance.usbSpeed.usb3') : t('performance.usbSpeed.usb2'),
        statusHex: status.toString(16).padStart(2, '0'),
        driverState: has16kDriver
          ? t('performance.usbSpeed.driverInstalled')
          : t('performance.usbSpeed.driverNotInstalled'),
        rawHex: bytesHex(raw),
      }),
      raw,
    }
  } catch {
    return { isUsb3: null, source: 'hid' }
  }
}

/**
 * WebUSB 校验检测：读取已授权 USB 设备的 usbVersionMajor（USB 描述符 bcdUSB 主版本），
 * 用于交叉验证 HID 自动检测（checkUsb3）的结果。
 */
export async function checkUsb3ViaWebUsb(device: unknown): Promise<UsbSpeedCheckResult> {
  const info = (
    device as {
      channel?: { deviceInfo?: { vendorId?: number; productId?: number } }
    }
  )?.channel?.deviceInfo
  try {
    const usb = (
      navigator as {
        usb?: {
          getDevices(): Promise<unknown[]>
          requestDevice(options: {
            filters: { vendorId: number; productId: number }[]
          }): Promise<unknown>
        }
      }
    ).usb
    if (!usb?.getDevices || !info?.vendorId || !info?.productId) {
      return { isUsb3: null, source: 'webusb' }
    }
    let match = (await usb.getDevices()).find(
      (d) =>
        (d as { vendorId?: number }).vendorId === info.vendorId &&
        (d as { productId?: number }).productId === info.productId,
    )
    if (!match) {
      try {
        match = await usb.requestDevice({
          filters: [{ vendorId: info.vendorId, productId: info.productId }],
        })
      } catch {
        // 用户取消授权或找不到设备
      }
    }
    if (!match) {
      return { isUsb3: null, source: 'webusb' }
    }
    const m = match as { usbVersionMajor?: number; usbVersionMinor?: number }
    const major = Number(m.usbVersionMajor ?? 0)
    const minor = Number(m.usbVersionMinor ?? 0)
    return {
      isUsb3: major >= 3,
      source: 'webusb',
      usbVersion: t('performance.usbSpeed.webUsbBcd', { major, minor }),
    }
  } catch {
    return { isUsb3: null, source: 'webusb' }
  }
}
